import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import postPaidManagement from "../../api/PostPaidManagement";
import Navbar from "../../components/dashboard/Navbar";
import Loader from "../../components/utils/Loader";
import PostPaidStatsCards from "../../components/postpaid/PostPaidStatsCards";
import PostPaidRequestTable from "../../components/postpaid/PostPaidRequestTable";
import RequestDetailsModal from "../../components/postpaid/RequestDetailsModal";
import { FileText, RefreshCw, Plus, AlertTriangle, CheckCircle, X, Loader2 } from "lucide-react";
import { useSelector } from "react-redux";
import { usePermissions } from "../../hooks/usePermissions";
import FeaturePermission from "../../components/permissions/FeaturePermission";
import { POST_PAID_PERMISSIONS } from "../../utils/permissions";

const PostPaidRequests = () => {
  const admin = useSelector((state) => state.admin.txtconnect_ng_admin);
  const { postPaidPermissions } = usePermissions();
  //console.log("admin", admin);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);
  const [requestToApprove, setRequestToApprove] = useState(null);

  // Fetch post-paid requests
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await postPaidManagement.getAllPostPaidRequests();

      if (response.status && response.data?.status_code === "000") {
        const requestsData = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setRequests(requestsData);
      } else {
        toast.error(
          response.data?.reason ||
            response.message ||
            "Failed to fetch custom-plan requests"
        );
        setRequests([]);
      }
    } catch (error) {
      console.error("Error fetching custom-plan requests:", error);
      toast.error("Failed to fetch custom-plan requests");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  //console.log("requests", requests);

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedRequest(null);
  };

  // Handle approval click - show confirmation
  const handleApproveClick = (request) => {
    if (!request.user_slug) {
      toast.error("Invalid request");
      return;
    }

    const approvalTier = !request.hod_approval && !request.finance_approval 
      ? "hod" 
      : request.hod_approval && !request.finance_approval 
      ? "finance" 
      : "completed";

    if (approvalTier === "completed") {
      toast.info("Request is already fully approved");
      return;
    }

    setRequestToApprove(request);
    setShowApprovalConfirm(true);
  };

  // Handle actual approval after confirmation
  const handleApproveConfirm = async () => {
    if (!requestToApprove) return;

    setApproving(true);
    
    try {
      const approvalTier = !requestToApprove.hod_approval && !requestToApprove.finance_approval 
        ? "hod" 
        : requestToApprove.hod_approval && !requestToApprove.finance_approval 
        ? "finance" 
        : "completed";

      const payload = { approve: true };
      const response = await postPaidManagement.PostPaidRequestApproval(
        payload,
        requestToApprove.postpaid_slug
      );

      if (response.status) {
        toast.success(
          `${approvalTier === "hod" ? "HOD" : "Finance"} approval successful!`
        );
        // Close confirmation modal
        setShowApprovalConfirm(false);
        setRequestToApprove(null);
        await fetchRequests();
        // Close details modal if open
        if (showDetailsModal) {
          setShowDetailsModal(false);
          setSelectedRequest(null);
        }
      } else {
        toast.error(
          response.data?.reason ||
            response.message ||
            "Failed to approve request"
        );
      }
    } catch (error) {
      console.error("Error approving request:", error);
      toast.error("Failed to approve request");
    } finally {
      setApproving(false);
    }
  };

  // Cancel approval
  const handleApproveCancel = () => {
    setShowApprovalConfirm(false);
    setRequestToApprove(null);
  };

  // Handle edit
  const handleEdit = (request) => {
    navigate(`/postpaid-requests/edit/${request.postpaid_slug}`);
  };

  // Calculate stats
  const stats = {
    total: requests.length,
    pending: requests.filter((r) => r.status === "pending").length,
    approved: requests.filter((r) => r.hod_approval && r.finance_approval)
      .length,
    hodApproved: requests.filter((r) => r.hod_approval).length,
    financeApproved: requests.filter((r) => r.finance_approval).length,
    totalAmount: requests.reduce(
      (sum, r) => sum + parseFloat(r.amount || 0),
      0
    ),
  };

  if (loading && requests.length === 0) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen bg-white">
          <Loader />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="px-4 py-8 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex gap-3 items-center mb-3">
                  <div className="p-3 bg-red-600 rounded-xl shadow-lg">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      Custom-Plan Requests
                    </h1>
                    <p className="text-gray-600">
                      Manage and review all custom-plan requests
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-center text-sm">
                  <span className="flex gap-2 items-center font-medium text-red-600">
                    <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                    {requests.length} total requests
                  </span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-500">
                    Last updated: {new Date().toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="flex gap-3">
                <FeaturePermission
                  permission={POST_PAID_PERMISSIONS.CREATE_REQUEST}
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate("/postpaid-requests/create")}
                    className="flex gap-2 items-center px-6 py-3 text-sm font-medium text-white bg-red-600 rounded-xl shadow-lg transition-colors hover:bg-red-700"
                  >
                    <Plus className="w-4 h-4" />
                    Create Request
                  </motion.button>
                </FeaturePermission>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={fetchRequests}
                  disabled={loading}
                  className="flex gap-2 items-center px-6 py-3 text-sm font-medium text-white bg-red-600 rounded-xl shadow-lg transition-colors hover:bg-red-700 disabled:opacity-50"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                  />
                  Refresh
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Stats Cards */}
          <PostPaidStatsCards stats={stats} />

          {/* Requests Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="overflow-hidden mt-8 bg-white rounded-2xl border border-gray-100 shadow-xl"
          >
            <PostPaidRequestTable
              data={requests}
              loading={loading}
              onViewRequest={handleViewRequest}
              onApprove={handleApproveClick}
              onEdit={handleEdit}
              permissions={postPaidPermissions}
            />
          </motion.div>
        </div>
      </div>

      {/* Request Details Modal */}
      {showDetailsModal && selectedRequest && (
        <RequestDetailsModal
          request={selectedRequest}
          onClose={closeDetailsModal}
          onApprove={handleApproveClick}
          onEdit={handleEdit}
          approving={approving}
        />
      )}

      {/* Approval Confirmation Modal */}
      {showApprovalConfirm && requestToApprove && (
        <div className="fixed inset-0 z-[10000] overflow-y-auto">
          <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 z-[9999]"
              onClick={handleApproveCancel}
            ></div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative z-[10000] inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
            >
              {/* Header */}
              <div className="flex justify-between items-center mb-4">
                <div className="flex gap-3 items-center">
                  <div className="p-3 bg-orange-100 rounded-xl">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Confirm Approval
                    </h3>
                    <p className="text-sm text-gray-500">
                      Please confirm this action
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleApproveCancel}
                  className="text-gray-400 transition-colors hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="mb-6">
                <p className="mb-4 text-sm text-gray-700">
                  Are you sure you want to approve this custom-plan request?
                </p>
                
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500">Customer:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {requestToApprove.customer_name || requestToApprove.customer_email}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500">Amount:</span>
                      <span className="text-sm font-semibold text-red-600">
                        GH₵{parseFloat(requestToApprove.amount || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500">SMS Units:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {requestToApprove.sms_units?.toLocaleString() || 0}
                      </span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">Approval Type:</span>
                        <span className="px-3 py-1 text-sm font-semibold text-white bg-blue-600 rounded-full">
                          {!requestToApprove.hod_approval && !requestToApprove.finance_approval
                            ? "HOD Approval"
                            : "Finance Approval"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleApproveCancel}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleApproveConfirm}
                  disabled={approving}
                  className="flex flex-1 gap-2 justify-center items-center px-4 py-3 text-sm font-medium text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {approving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Confirm Approval
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </>
  );
};

export default PostPaidRequests;
